/* Save cart edits, then navigate to standard checkout without a wallet redirect. */
(() => {
  if (window.zypherCartCheckoutReady) return;
  window.zypherCartCheckoutReady = true;
  let opening = false;
  let lockedCart = null;
  let previousInert = false;

  function releaseCart() {
    if (lockedCart) lockedCart.inert = previousInert;
    lockedCart = null;
  }

  function cartItemsFor(form) {
    const drawer = form.closest('cart-drawer');
    return drawer ? drawer.querySelector('cart-drawer-items') : document.querySelector('cart-items');
  }

  function checkoutForm(link) {
    return document.getElementById(link.dataset.cartForm || 'ZypherCartCheckout');
  }

  function checkoutLink(formId) {
    return [...document.querySelectorAll('[data-zy-checkout]')].find(link => checkoutForm(link)?.id === formId);
  }

  function waitForCart(items) {
    let observer;
    let timeout;
    const pendingUpdate = items?.flushPendingChanges?.();
    const currentItems = () => items?.querySelector('#main-cart-items, #CartDrawer-CartItems');
    const idle = !currentItems()?.classList.contains('cart__items--disabled') ? Promise.resolve() : new Promise((resolve) => {
      observer = new MutationObserver(() => {
        if (!currentItems()?.classList.contains('cart__items--disabled')) resolve();
      });
      observer.observe(items, {
        attributes: true, attributeFilter: ['class'], childList: true, subtree: true
      });
    });
    return Promise.race([
      Promise.all([pendingUpdate, idle]),
      new Promise((resolve, reject) => {
        timeout = setTimeout(() => reject(new Error('Cart update timed out')), 12000);
      })
    ]).finally(() => {
      observer?.disconnect();
      clearTimeout(timeout);
    });
  }

  async function checkout(form, link) {
    if (opening) return;
    const drawer = form.closest('cart-drawer');
    const destination = link.href;
    const label = link.querySelector('[data-zy-checkout-label]');
    const previousLabel = label.textContent;
    link.dataset.checkoutLabel = previousLabel;
    const errorId = drawer ? 'CartDrawer-CartErrors' : 'cart-errors';
    const errors = document.getElementById(errorId);
    const failureMessage = form.dataset.errorMessage;
    opening = true;
    if (errors) errors.textContent = '';
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('aria-busy', 'true');
    label.textContent = form.dataset.openingLabel;
    const controller = new AbortController();
    let requestTimeout;
    try {
      await waitForCart(cartItemsFor(form));
      const currentForm = document.getElementById(form.id);
      if (!currentForm || !currentForm.reportValidity()) throw new Error(failureMessage);
      const items = cartItemsFor(currentForm);
      const inputs = [...(items?.querySelectorAll('.quantity__input') || [])];
      if (!inputs.length) throw new Error(failureMessage);
      const updates = {};
      for (const input of inputs) {
        const quantity = Number(input.value);
        if (!input.value.trim() || !input.dataset.cartLineKey || !Number.isInteger(quantity) || quantity < 0 || !input.checkValidity()) {
          throw new Error(failureMessage);
        }
        updates[input.dataset.cartLineKey] = quantity;
      }
      const note = document.getElementById(drawer ? 'CartDrawer-Note' : 'Cart-note');
      const payload = { updates };
      if (note) payload.note = note.value;
      lockedCart = items;
      previousInert = Boolean(items.inert);
      items.inert = true;
      requestTimeout = setTimeout(() => controller.abort(), 12000);
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || !cart.item_count) throw new Error(failureMessage);
      window.location.assign(destination);
    } catch (error) {
      releaseCart();
      opening = false;
      const currentLink = checkoutLink(form.id);
      if (currentLink) {
        currentLink.removeAttribute('aria-disabled');
        currentLink.removeAttribute('aria-busy');
        currentLink.querySelector('[data-zy-checkout-label]').textContent = previousLabel;
      }
      const currentErrors = document.getElementById(errorId);
      if (currentErrors) {
        currentErrors.textContent = failureMessage;
        currentErrors.classList.remove('visually-hidden');
        currentErrors.setAttribute('tabindex', '-1');
        currentErrors.focus();
      }
    } finally {
      clearTimeout(requestTimeout);
    }
  }

  window.addEventListener('click', event => {
    const link = event.target.closest?.('[data-zy-checkout]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const form = checkoutForm(link);
    if (!form) return;
    event.preventDefault();
    // This link owns checkout navigation; leave theme loaders and wallet
    // click interception out of the save-and-redirect handoff.
    event.stopImmediatePropagation();
    checkout(form, link);
  }, true);

  window.addEventListener('submit', event => {
    if (!['ZypherCartCheckout', 'CartDrawer-Form'].includes(event.target.id)) return;
    const link = checkoutLink(event.target.id);
    if (!link) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(event.target, link);
  }, true);

  window.addEventListener('pageshow', () => {
    releaseCart();
    opening = false;
    document.querySelectorAll('[data-zy-checkout]').forEach(link => {
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');
      if (link.dataset.checkoutLabel) link.querySelector('[data-zy-checkout-label]').textContent = link.dataset.checkoutLabel;
    });
  });
})();
